import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GRUSHKA_ORGANIZATION_ID } from '@app/access/constants/organizations';
import {
  type GetRecordSlotForClientResponse,
  useLazyGetAutoReplyForClientQuery,
  useLazyGetRecordSlotForClientQuery,
} from '@app/api/Leads';
import {
  type ModelMessageTemplateLite,
  useGetFilesMutation,
  useLazySearchForSystemQuery,
} from '@app/api/MessageTemplates';
import {
  getRecommendChatErrorMessage,
  getRecommendChatReason,
} from '@app/api/RecommendChat/errors';
import { useCreateRecordByAiRequestIdMutation } from '@app/api/Records/api';
import { useSendMessageToClientMutation } from '@app/api/SocialIntegrations';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { type LeadCardIntegrationItemData } from '@app/components/LeadCard/data';
import type { LeadMessageAttachment } from '@app/components/LeadCard/ui/MessageComposer/types';
import {
  createTemplateAttachment,
  filterMessageTemplates,
  getAllowedAttachmentExtensions,
  getIsAllowedAttachmentExtension,
  getIsAttachmentsSizeExceeded,
  getIsSendDisabled,
  getMessageMaxLengthBySocialType,
  getTemplateSearchLimit,
  getTemplateSearchQuery,
  insertTextAtCursor,
  normalizeFilesToLeadMessageAttachments,
  replaceTemplateVariables,
} from '@app/components/LeadCard/ui/MessageComposer/utils';
import { getSendMessageToClientError } from '@app/components/LeadCard/utils';
import { config } from '@app/config';
import {
  ACTIVE,
  FRONT_WARNING,
  SMS_SERVICE,
  SUCCESS,
  WHATSAPP,
  WARNING,
  LOYALTY_PROGRAM_SENDOUTS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { useLeadMessageSendAttempt } from './useLeadMessageSendAttempt';

import type { TextAreaRef } from 'antd/es/input/TextArea';
import type { ChangeEvent, KeyboardEvent } from 'react';

const TEMPLATE_SEARCH_DEBOUNCE_MS = 300;

// Таймаут запроса отправки (SEND_MESSAGE_TO_CLIENT_TIMEOUT_MS в мутации)
// прилетает как axios-ошибка с текстом «timeout of Nms exceeded». Для
// оператора это отдельный случай: бэк мог успеть отправить сообщение,
// поэтому уведомление просит проверить чат, а не повторять вслепую.
const isSendTimeoutError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'data' in error &&
      typeof (error as { data?: unknown }).data === 'string' &&
      /timeout of \d+ms exceeded/i.test((error as { data: string }).data)
  );

const normalizeIntegrationValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const normalizedValue = String(value).trim().toLowerCase();

  return normalizedValue.length > 0 ? normalizedValue : null;
};

const findIntegrationByBackendValue = (
  integrations: LeadCardIntegrationItemData[],
  value: unknown
): LeadCardIntegrationItemData | null => {
  const normalizedValue = normalizeIntegrationValue(value);

  if (normalizedValue === null) return null;

  const integrationById = integrations.find(
    ({ id }) => normalizeIntegrationValue(id) === normalizedValue
  );

  if (integrationById) return integrationById;

  const integrationsByDescription = integrations.filter(({ type, name }) =>
    [type, name].some(
      (integrationValue) =>
        normalizeIntegrationValue(integrationValue) === normalizedValue
    )
  );

  return integrationsByDescription.length === 1
    ? integrationsByDescription[0]
    : null;
};

const getDefaultIntegrationId = ({
  integrations,
  lastSocialIntegration,
  lastSocialIntegrationId,
}: {
  integrations: LeadCardIntegrationItemData[];
  lastSocialIntegration?: number | null;
  lastSocialIntegrationId?: number | null;
}): LeadCardIntegrationItemData['id'] | null => {
  const integrationByLastId = findIntegrationByBackendValue(
    integrations,
    lastSocialIntegrationId
  );

  if (integrationByLastId) return integrationByLastId.id;

  const integrationByLastValue = findIntegrationByBackendValue(
    integrations,
    lastSocialIntegration
  );

  return integrationByLastValue?.id ?? integrations[0]?.id ?? null;
};

const getClientFullName = ({
  clientFirstName,
  clientLastName,
  clientMiddleName,
}: {
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientMiddleName?: string | null;
}): string | null => {
  const fullName = [clientLastName, clientFirstName, clientMiddleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return fullName || null;
};

export const useLeadMessageComposer = () => {
  const {
    leadCardData,
    isBranchesLoading,
    refetchLeadCard,
    refreshLatestHistory,
    selectedLeadId,
    selectedLeadIntegrations,
  } = useLeadCardContext();

  const {
    params: { ActiveBranchId: activeBranchId },
    userId,
    database,
  } = useUserPrint();
  const { showNotification } = useNotification();

  const textAreaRef = useRef<TextAreaRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const sendInFlightRef = useRef(false);
  const initializedIntegrationLeadIdRef = useRef<number | null>(null);
  // Токен последнего запроса слота записи. recordSlotHandler и сброс по
  // selectedLeadId увеличивают его перед стартом нового цикла; колбэки
  // GetRecordSlotForClient применяют свой результат, только если токен всё
  // ещё совпадает — иначе это ответ на лид/открытие, которые уже неактуальны
  // (см. getSendAttempt/isCurrentSendAttempt у отправки сообщения).
  const recordSlotRequestTokenRef = useRef(0);

  const defaultSelectedIntegrationId = useMemo(() => {
    if (!selectedLeadId || leadCardData?.LeadId !== selectedLeadId) {
      return null;
    }

    return getDefaultIntegrationId({
      integrations: selectedLeadIntegrations,
      lastSocialIntegration: leadCardData.ClientView.LastSocialIntegration,
      lastSocialIntegrationId: leadCardData.ClientView.LastSocialIntegrationId,
    });
  }, [
    leadCardData?.ClientView.LastSocialIntegration,
    leadCardData?.ClientView.LastSocialIntegrationId,
    leadCardData?.LeadId,
    selectedLeadId,
    selectedLeadIntegrations,
  ]);

  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState<LeadMessageAttachment[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    LeadCardIntegrationItemData['id'] | null
  >(defaultSelectedIntegrationId);
  const [isIntegrationManuallySelected, setIsIntegrationManuallySelected] =
    useState(false);
  const [templates, setTemplates] = useState<ModelMessageTemplateLite[]>([]);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isBranchConfirmationOpen, setIsBranchConfirmationOpen] =
    useState(false);
  const [isRecordSlotModalOpen, setIsRecordSlotModalOpen] = useState(false);
  // Идентификатор авто-ответа ИИ, который сейчас лежит в поле ввода. Живёт
  // ровно столько же, сколько сам текст: как только текст заменили, очистили
  // или отправили — идентификатор больше не относится к содержимому поля.
  const [aiAutoReplyTraceId, setAiAutoReplyTraceId] = useState<string | null>(
    null
  );

  const [searchTemplates] = useLazySearchForSystemQuery();
  const [fetchMessageTemplateFiles, { isLoading: isFetchingTemplateFiles }] =
    useGetFilesMutation();
  const [sendMessageToClient, { isLoading: isSending }] =
    useSendMessageToClientMutation();
  const [getAutoReplyForClient, { isFetching: isAutoReplyLoading }] =
    useLazyGetAutoReplyForClientQuery();
  // Результат слота ведём в собственном состоянии, а не через data/currentData
  // хука: currentData всё равно держит прошлый успешный ответ для той же
  // комбинации аргументов, пока не придёт новый, — при повторном открытии
  // модалки для того же клиента под спиннером было видно содержимое
  // прошлого открытия. Сбрасываем всё в recordSlotHandler при каждом
  // открытии, поэтому модалка всегда стартует пустой.
  const [getRecordSlotForClient] = useLazyGetRecordSlotForClientQuery();
  const [recordSlotData, setRecordSlotData] = useState<
    GetRecordSlotForClientResponse | undefined
  >(undefined);
  const [recordSlotError, setRecordSlotError] = useState<unknown>(undefined);
  const [isRecordSlotLoading, setIsRecordSlotLoading] = useState(false);
  const [createRecordByAiRequestId, { isLoading: isCreatingRecordFromSlot }] =
    useCreateRecordByAiRequestIdMutation();

  const selectedIntegration = useMemo(
    () =>
      selectedLeadIntegrations.find(({ id }) => id === selectedIntegrationId) ??
      null,
    [selectedLeadIntegrations, selectedIntegrationId]
  );

  const selectedFullIntegration = useMemo(
    () =>
      (leadCardData?.SocialIntegrations ?? []).find(
        ({ Id }) => Id === Number(selectedIntegrationId)
      ) ?? null,
    [leadCardData?.SocialIntegrations, selectedIntegrationId]
  );

  const isRecommendChat =
    selectedFullIntegration?.SocialType === LOYALTY_PROGRAM_SENDOUTS;
  const recommendCapability = selectedFullIntegration?.RecommendChat;
  const isTextOnly = isRecommendChat;
  const unavailableReason =
    isRecommendChat && recommendCapability?.available !== true
      ? getRecommendChatReason(recommendCapability?.reasonCode)
      : null;

  const messageMaxLength = useMemo(
    () =>
      recommendCapability?.maxTextLength ??
      getMessageMaxLengthBySocialType(selectedFullIntegration?.SocialType),
    [recommendCapability?.maxTextLength, selectedFullIntegration?.SocialType]
  );

  // P0 measures the limit in UTF-16 units, matching C# string.Length.
  const messageLength = messageText.length;
  const sendBlockedReason =
    unavailableReason ??
    (isTextOnly && attachments.length > 0
      ? 'В Рекомендуй можно отправить только текст. Удалите вложения.'
      : isRecommendChat && messageLength > messageMaxLength
      ? `Сообщение не должно превышать ${messageMaxLength} символов.`
      : null);

  const clientView = leadCardData?.ClientView ?? null;
  const leadClientId = leadCardData?.ClientId ?? clientView?.Id ?? null;
  const recipientKey = JSON.stringify([
    database,
    userId,
    activeBranchId,
    selectedLeadId,
    leadClientId,
    selectedIntegrationId,
    clientView?.Phone1,
  ]);
  const draftKey = JSON.stringify([
    recipientKey,
    messageText,
    aiAutoReplyTraceId,
    attachments.map(({ id }) => id),
  ]);
  const { getSendAttempt, isCurrentSendAttempt } =
    useLeadMessageSendAttempt(draftKey);
  const currentDraftKeyRef = useRef(draftKey);
  currentDraftKeyRef.current = draftKey;
  const currentRecipientKeyRef = useRef(recipientKey);
  currentRecipientKeyRef.current = recipientKey;
  const templateSelectionRef = useRef(0);

  const templateVariablesData = useMemo(
    () => ({
      branchName: selectedFullIntegration?.Branch ?? null,
      clientFirstName: clientView?.NameFirst ?? null,
      clientLastName: clientView?.NameLast ?? null,
      clientMiddleName: clientView?.NameMiddle ?? null,
      clientFullName: getClientFullName({
        clientFirstName: clientView?.NameFirst,
        clientLastName: clientView?.NameLast,
        clientMiddleName: clientView?.NameMiddle,
      }),
      clientId: leadCardData?.ClientId ?? clientView?.Id ?? null,
      email: clientView?.Email ?? null,
      leadId: selectedLeadId,
      phone: clientView?.Phone1 ?? null,
      responsibleName: leadCardData?.Lead?.UserName ?? null,
    }),
    [
      clientView,
      leadCardData?.ClientId,
      leadCardData?.Lead?.UserName,
      selectedFullIntegration?.Branch,
      selectedLeadId,
    ]
  );

  const templateSearchQuery = useMemo(
    () => getTemplateSearchQuery(messageText),
    [messageText]
  );

  const acceptedAttachmentExtensions = useMemo(
    () => getAllowedAttachmentExtensions().map((extension) => `.${extension}`),
    []
  );

  const isTemplateFilesLoading = isFetchingTemplateFiles;

  const hasNoIntegrations =
    !isBranchesLoading && selectedLeadIntegrations.length === 0;

  const isInputDisabled =
    isBranchesLoading ||
    isSending ||
    isTemplateFilesLoading ||
    hasNoIntegrations ||
    unavailableReason !== null;

  const isAutoReplyDisabled =
    isInputDisabled || isAutoReplyLoading || leadClientId === null;

  // Авто-ответ ИИ на проде доступен только организации GRUSHKA; на dev и
  // stage — всем (для тестирования). Гейт условен по среде, поэтому единый
  // код работает во всех сборках. Эталон гейта — clogLeadsRegistryDebug.
  // Дополнительно бэк может выключить авто-ответ для конкретного лида через
  // IsAiAutoReply в GetLeadCard (настройки ИИ-функций организации).
  const isAiAutoReplyAvailable =
    (config.mode !== 'production' || database === GRUSHKA_ORGANIZATION_ID) &&
    leadCardData?.IsAiAutoReply !== false;

  // Кнопку «Записать клиента с помощью ИИ» показываем строго по флагу
  // IsAiCreateRecord из GetLeadCard (настройки ИИ-функций организации).
  const isAiCreateRecordAvailable = leadCardData?.IsAiCreateRecord === true;

  const isRecordSlotDisabled =
    isInputDisabled || isRecordSlotLoading || leadClientId === null;

  const isSendDisabled =
    isBranchesLoading ||
    sendBlockedReason !== null ||
    getIsSendDisabled({
      attachments,
      isSending,
      isUploadingFiles,
      messageText,
      selectedIntegrationId,
      selectedLeadId,
    });

  const isSelectedLeadWithoutBranch =
    !isRecommendChat && selectedFullIntegration?.IsClientInBranch === false;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setMessageText('');
    setAttachments([]);
    setTemplates([]);
    setSelectedIntegrationId(null);
    setIsIntegrationManuallySelected(false);
    setAiAutoReplyTraceId(null);
    // Инвалидирует ответ на любой ещё не завершившийся запрос слота записи
    // для прежнего лида — иначе он мог бы прилететь уже после переключения
    // и подставиться в модалку нового лида (см. recordSlotRequestTokenRef).
    recordSlotRequestTokenRef.current += 1;
    setIsRecordSlotModalOpen(false);
    setRecordSlotData(undefined);
    setRecordSlotError(undefined);
    setIsRecordSlotLoading(false);
    initializedIntegrationLeadIdRef.current = null;
  }, [selectedLeadId]);

  useEffect(() => {
    if (!selectedLeadId || defaultSelectedIntegrationId === null) return;
    if (isIntegrationManuallySelected) return;
    if (initializedIntegrationLeadIdRef.current === selectedLeadId) return;

    initializedIntegrationLeadIdRef.current = selectedLeadId;
    setSelectedIntegrationId(defaultSelectedIntegrationId);
  }, [
    defaultSelectedIntegrationId,
    isIntegrationManuallySelected,
    selectedLeadId,
  ]);

  useEffect(() => {
    if (!activeBranchId || !templateSearchQuery) {
      setTemplates([]);
      setIsTemplatesLoading(false);
      return;
    }

    let isActual = true;

    const timeoutId = window.setTimeout(() => {
      const requestTemplates = async () => {
        setIsTemplatesLoading(true);

        try {
          const response = await searchTemplates({
            ActiveBranchId: activeBranchId,
            search: templateSearchQuery,
            count: getTemplateSearchLimit(),
          }).unwrap();

          if (!isActual) return;

          setTemplates(
            filterMessageTemplates(response ?? [], templateSearchQuery)
          );
          setIsTemplatesOpen(true);
        } catch {
          if (!isActual) return;

          setTemplates([]);
        } finally {
          if (!isActual) return;

          setIsTemplatesLoading(false);
        }
      };

      void requestTemplates();
    }, TEMPLATE_SEARCH_DEBOUNCE_MS);

    return () => {
      isActual = false;
      window.clearTimeout(timeoutId);
    };
  }, [activeBranchId, searchTemplates, templateSearchQuery]);

  const focusInput = useCallback((cursorPosition?: number) => {
    window.requestAnimationFrame(() => {
      const textArea = textAreaRef.current?.resizableTextArea?.textArea;
      if (!textArea) return;

      textArea.focus();

      if (typeof cursorPosition === 'number') {
        textArea.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  }, []);

  const messageChangeHandler = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(value);
      setIsTemplatesOpen(true);

      // Правку авто-ответа считаем его использованием, а вот очистку поля —
      // отказом: отправлять после неё будем уже своё сообщение.
      if (value.trim().length === 0) setAiAutoReplyTraceId(null);
    },
    []
  );

  const emojiSelectHandler = useCallback(
    (emoji: string) => {
      const textArea = textAreaRef.current?.resizableTextArea?.textArea;
      const { value, cursorPosition } = insertTextAtCursor(
        messageText,
        emoji,
        textArea?.selectionStart,
        textArea?.selectionEnd
      );

      setMessageText(value);
      focusInput(cursorPosition);
    },
    [focusInput, messageText]
  );

  const templateSelectHandler = useCallback(
    async (template: ModelMessageTemplateLite) => {
      const templateSelection = ++templateSelectionRef.current;
      const resolvedMessage = replaceTemplateVariables(
        template.Message ?? '',
        templateVariablesData
      );

      setMessageText(resolvedMessage);
      setIsTemplatesOpen(false);
      setAiAutoReplyTraceId(null);
      focusInput(resolvedMessage.length);

      if (!template.FileIds || template.FileIds.length === 0) return;

      if (isTextOnly) {
        showNotification(
          'Добавлен текст шаблона. Вложения в Рекомендуй не поддерживаются.',
          FRONT_WARNING
        );
        return;
      }

      if (!activeBranchId) {
        showNotification(
          'Не удалось определить филиал для загрузки файлов шаблона',
          FRONT_WARNING
        );
        return;
      }

      try {
        const recipientBeforeLoading = currentRecipientKeyRef.current;
        const response = await fetchMessageTemplateFiles({
          ActiveBranchId: activeBranchId,
          data: {
            MessageTemplates: [
              {
                MessageTemplateId: template.Id,
                FileIds: template.FileIds,
              },
            ],
          },
        }).unwrap();

        if (
          !isMountedRef.current ||
          currentRecipientKeyRef.current !== recipientBeforeLoading ||
          templateSelection !== templateSelectionRef.current
        )
          return;

        const [templateWithFiles] = response.MessageTemplates ?? [];
        const templateAttachments = (templateWithFiles?.Files ?? []).map(
          ({ Data, Description, Name }) =>
            createTemplateAttachment({
              data: Data ?? null,
              description: Description ?? null,
              fileName: Name ?? null,
            })
        );

        if (templateAttachments.length === 0) return;

        setAttachments((prev) => [...prev, ...templateAttachments]);
      } catch (error) {
        showNotification(error, FRONT_WARNING);
      }
    },
    [
      activeBranchId,
      isTextOnly,
      fetchMessageTemplateFiles,
      focusInput,
      showNotification,
      templateVariablesData,
    ]
  );

  // ИИ готовит ответ по переписке с клиентом. Запрос может идти несколько
  // секунд, поэтому кнопка остаётся со спиннером, а результат кладётся в поле
  // ввода тем же способом, что и текст шаблона.
  const autoReplyHandler = useCallback(async () => {
    if (isAutoReplyLoading) return;

    if (!leadClientId) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    try {
      const draftBeforeLoading = currentDraftKeyRef.current;
      const { Message, TraceId } = await getAutoReplyForClient({
        ActiveBranchId: activeBranchId,
        clientId: leadClientId,
      }).unwrap();

      if (
        !isMountedRef.current ||
        currentDraftKeyRef.current !== draftBeforeLoading
      )
        return;

      const autoReplyMessage = Message?.trim() ?? '';

      // Пустой ответ поле ввода не меняет, поэтому и сохранённый TraceId
      // остаётся прежним — он всё ещё описывает то, что видит пользователь.
      if (!autoReplyMessage) {
        showNotification(
          'Не удалось сформировать ответ клиенту',
          FRONT_WARNING
        );
        return;
      }

      setMessageText(autoReplyMessage);
      setIsTemplatesOpen(false);
      setAiAutoReplyTraceId(TraceId?.trim() ? TraceId : null);
      focusInput(autoReplyMessage.length);
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    focusInput,
    getAutoReplyForClient,
    isAutoReplyLoading,
    leadClientId,
    showNotification,
  ]);

  // ИИ подбирает слот для записи клиента по переписке. Запрос идёт лениво при
  // открытии модалки; ошибку показываем внутри неё, а не тостом, чтобы оператор
  // видел контекст.
  const recordSlotHandler = useCallback(async () => {
    if (!leadClientId) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    // Токен этого конкретного открытия — колбэки ниже применяют свой
    // результат, только если он всё ещё актуален (см. комментарий у
    // recordSlotRequestTokenRef).
    const requestToken = ++recordSlotRequestTokenRef.current;

    setIsRecordSlotModalOpen(true);
    // Сброс перед запросом — модалка стартует пустой, а не с содержимым
    // прошлого открытия (см. комментарий у объявления состояния выше).
    setRecordSlotData(undefined);
    setRecordSlotError(undefined);
    setIsRecordSlotLoading(true);

    const isCurrentRequest = () =>
      isMountedRef.current &&
      requestToken === recordSlotRequestTokenRef.current;

    try {
      const data = await getRecordSlotForClient({
        ActiveBranchId: activeBranchId,
        clientId: leadClientId,
      }).unwrap();

      if (!isCurrentRequest()) return;
      setRecordSlotData(data);
    } catch (error) {
      if (!isCurrentRequest()) return;
      setRecordSlotError(error);
    } finally {
      if (isCurrentRequest()) {
        setIsRecordSlotLoading(false);
      }
    }
  }, [activeBranchId, getRecordSlotForClient, leadClientId, showNotification]);

  const closeRecordSlotModal = useCallback(() => {
    setIsRecordSlotModalOpen(false);
  }, []);

  // Подтверждение слота, подобранного ИИ: создаём запись по сохранённому
  // RequestId. Успех/ошибка — тостом (плашкой), как описано в ТЗ; модалку
  // закрываем только при успехе, чтобы при ошибке оператор мог повторить
  // попытку или уйти в ручной сценарий.
  const createRecordFromSlotHandler = useCallback(async () => {
    if (!recordSlotData?.RequestId) {
      showNotification('Не удалось определить слот для записи', FRONT_WARNING);
      return;
    }

    // Без телефона автоматическое создание запрещено — в модалке в этом
    // случае и кнопки «Создать» нет, но дублируем проверку здесь, чтобы
    // запрет не зависел только от того, что кнопка не отрисовалась.
    if (!recordSlotData.Phone1?.trim()) {
      showNotification('У клиента не указан номер телефона', FRONT_WARNING);
      return;
    }

    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    try {
      await createRecordByAiRequestId({
        ActiveBranchId: activeBranchId,
        data: recordSlotData.RequestId,
      }).unwrap();

      if (!isMountedRef.current) return;

      showNotification('Запись успешно создана', SUCCESS);
      setIsRecordSlotModalOpen(false);
      refetchLeadCard();
      void refreshLatestHistory();
    } catch (error) {
      if (!isMountedRef.current) return;

      showNotification(error);
    }
  }, [
    activeBranchId,
    createRecordByAiRequestId,
    recordSlotData?.Phone1,
    recordSlotData?.RequestId,
    refetchLeadCard,
    refreshLatestHistory,
    showNotification,
  ]);

  const uploadAttachmentsHandler = useCallback(
    async ({ target }: ChangeEvent<HTMLInputElement>) => {
      const files = target.files ? Array.from(target.files) : [];
      target.value = '';

      if (files.length === 0) return;
      if (isTextOnly) {
        showNotification(
          'В Рекомендуй можно отправить только текст.',
          FRONT_WARNING
        );
        return;
      }

      const allowedFiles = files.filter(({ name }) =>
        getIsAllowedAttachmentExtension(name)
      );

      if (allowedFiles.length === 0) {
        showNotification(
          'Данный файл не может быть отправлен. Проверьте формат загружаемого файла.',
          FRONT_WARNING
        );
        return;
      }

      setIsUploadingFiles(true);

      try {
        const recipientBeforeLoading = currentRecipientKeyRef.current;
        const normalizedAttachments =
          await normalizeFilesToLeadMessageAttachments(allowedFiles);
        if (
          !isMountedRef.current ||
          currentRecipientKeyRef.current !== recipientBeforeLoading
        )
          return;
        const nextAttachments = [...attachments, ...normalizedAttachments];

        if (getIsAttachmentsSizeExceeded(nextAttachments)) {
          showNotification('Суммарный размер файлов превышает 300 МБ', WARNING);
          return;
        }

        setAttachments(nextAttachments);
      } catch (error) {
        showNotification(error, FRONT_WARNING);
      } finally {
        if (isMountedRef.current) {
          setIsUploadingFiles(false);
        }
      }
    },
    [attachments, isTextOnly, showNotification]
  );

  const attachFilesHandler = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const removeAttachmentHandler = useCallback((attachmentId: string) => {
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
  }, []);

  const integrationSelectHandler = useCallback(
    ({ id }: LeadCardIntegrationItemData) => {
      setIsIntegrationManuallySelected(true);
      setSelectedIntegrationId(id);
    },
    []
  );

  const sendMessageHandler = useCallback(async () => {
    if (isSending || sendInFlightRef.current) return;

    if (sendBlockedReason) {
      showNotification(sendBlockedReason, FRONT_WARNING);
      return;
    }

    const trimmedMessage = messageText.trim();

    if (!trimmedMessage && attachments.length === 0) {
      showNotification('Введите сообщение или прикрепите файл', FRONT_WARNING);
      return;
    }

    if (!selectedLeadId) {
      showNotification('Лид не был распознан', FRONT_WARNING);
      return;
    }

    if (!activeBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    if (!selectedFullIntegration) {
      showNotification('Идентификатор интеграции не найден', FRONT_WARNING);
      return;
    }

    if (selectedFullIntegration.Status !== ACTIVE) {
      showNotification('Выберите активную интеграцию', FRONT_WARNING);
      return;
    }

    if (selectedFullIntegration.IsBlocked) {
      showNotification(
        'Клиент заблокировал выбранную интеграцию',
        FRONT_WARNING
      );
      return;
    }

    const clientId = leadCardData?.ClientId ?? clientView?.Id ?? null;
    const phone = clientView?.Phone1 ?? null;

    if (!clientId) {
      showNotification('Идентификатор клиента не найден', FRONT_WARNING);
      return;
    }

    if (
      (selectedFullIntegration.SocialType === WHATSAPP ||
        selectedFullIntegration.SocialType === SMS_SERVICE) &&
      !phone
    ) {
      showNotification('Номер телефона клиента не найден', FRONT_WARNING);
      return;
    }

    const sendAttempt = getSendAttempt();
    sendInFlightRef.current = true;

    try {
      await sendMessageToClient({
        ActiveBranchId: activeBranchId,
        // Лог-заголовки (HeaderUserId/HeaderDatabase) — паритет с легаси-слоем:
        // по ним на бэке разбираются инциденты отправки.
        logs: { isLogged: true, userId, database },
        data: {
          IntegrationId: selectedFullIntegration.Id,
          Destination: {
            Phone: phone,
            ClientId: clientId,
            LeadId: selectedLeadId,
          },
          Message: trimmedMessage || null,
          Attachments:
            attachments.length > 0
              ? attachments.map(
                  ({ Data, Description, FileName, FileSize }) => ({
                    Data,
                    Description,
                    FileName,
                    FileSize,
                  })
                )
              : null,
          IsSendout: false,
          // Непустой TraceId говорит бэку, что отправленный текст подготовил
          // ИИ — по нему считается фидбэк использования авто-ответа.
          AiMessageTraceId: aiAutoReplyTraceId,
          ...(isRecommendChat
            ? { ClientRequestId: sendAttempt.requestId }
            : {}),
        },
      }).unwrap();

      if (!isMountedRef.current || !isCurrentSendAttempt(sendAttempt)) return;

      setMessageText('');
      setAttachments([]);
      setIsBranchConfirmationOpen(false);
      setTemplates([]);
      setAiAutoReplyTraceId(null);

      refetchLeadCard();
      void refreshLatestHistory();
    } catch (error) {
      if (!isMountedRef.current || !isCurrentSendAttempt(sendAttempt)) return;
      if (isSendTimeoutError(error)) {
        showNotification(
          'CRM не ответила — проверьте чат перед повторной отправкой',
          FRONT_WARNING
        );
        return;
      }

      showNotification(
        isRecommendChat
          ? getRecommendChatErrorMessage(error)
          : getSendMessageToClientError(error)
      );
    } finally {
      sendInFlightRef.current = false;
    }
  }, [
    activeBranchId,
    aiAutoReplyTraceId,
    attachments,
    clientView?.Id,
    clientView?.Phone1,
    database,
    isSending,
    isRecommendChat,
    getSendAttempt,
    isCurrentSendAttempt,
    sendBlockedReason,
    leadCardData?.ClientId,
    messageText,
    refetchLeadCard,
    refreshLatestHistory,
    selectedFullIntegration,
    selectedLeadId,
    sendMessageToClient,
    showNotification,
    userId,
  ]);

  const prepareSendMessageHandler = useCallback(() => {
    if (isSelectedLeadWithoutBranch) {
      setIsBranchConfirmationOpen(true);
      return;
    }

    void sendMessageHandler();
  }, [isSelectedLeadWithoutBranch, sendMessageHandler]);

  const messageKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      const isSendHotkey =
        (event.ctrlKey || event.metaKey) && event.key === 'Enter';

      if (!isSendHotkey) return;

      event.preventDefault();

      if (isSendDisabled) return;

      prepareSendMessageHandler();
    },
    [isSendDisabled, prepareSendMessageHandler]
  );

  return {
    acceptedAttachmentExtensions,
    attachFilesHandler,
    attachments,
    autoReplyHandler,
    closeRecordSlotModal,
    createRecordFromSlotHandler,
    fileInputRef,
    hasNoIntegrations,
    isAiAutoReplyAvailable,
    isAiCreateRecordAvailable,
    isAutoReplyDisabled,
    isAutoReplyLoading,
    isCreatingRecordFromSlot,
    isRecordSlotDisabled,
    isRecordSlotLoading,
    isRecordSlotModalOpen,
    recordSlotData,
    recordSlotError,
    recordSlotHandler,
    isBranchConfirmationOpen,
    isBranchesLoading,
    isInputDisabled,
    isRecommendChat,
    isTextOnly,
    messageLength,
    sendBlockedReason,
    isSendDisabled,
    isSending,
    isTemplateFilesLoading,
    isTemplatesLoading,
    isTemplatesOpen,
    isUploadingFiles,
    messageChangeHandler,
    messageMaxLength,
    messageText,
    removeAttachmentHandler,
    selectedFullIntegration,
    selectedIntegration,
    selectedLeadIntegrations,
    sendMessageHandler,
    setIsBranchConfirmationOpen,
    setIsTemplatesOpen,
    integrationSelectHandler,
    emojiSelectHandler,
    templateSelectHandler,
    templates,
    textAreaRef,
    uploadAttachmentsHandler,
    prepareSendMessageHandler,
    messageKeyDownHandler,
  };
};
